/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { Grid, Skeleton, Typography, Typography2, VisuallyHidden, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { SkillAssessmentsTestResults } from 'bundles/account-profile/common/types';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import {
  getColorByProficiency,
  getProficiencyIdToLabel,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/utils';

import _t from 'i18n!nls/account-profile';

const styles = {
  skillsDataContainer: (theme: Theme) => css`
    margin-top: ${theme.spacing(32)};
    border: 1px solid ${theme.palette.gray[300]};
    border-radius: 8px;

    ${theme.breakpoints.down('xs')} {
      border: none;
    }
  `,
  overallScoreBackground: (chosenColor: string) => css`
    background-color: ${chosenColor};
  `,
  overallScoreSection: (theme: Theme) => css`
    padding: 16px;
    border-right: 1px solid ${theme.palette.gray[300]};

    ${theme.breakpoints.down('xs')} {
      min-height: 95px;
      border-radius: 8px 8px 0 0;
      border: 1px solid ${theme.palette.gray[300]};
    }
  `,
  filledEllipse: (chosenColor: string) => css`
    background-color: ${chosenColor};
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 8px;
  `,
  skillsList: (theme: Theme) => css`
    list-style-type: none;
    padding: 16px 16px 16px;
    width: 100%;
    margin-bottom: 0;
    ${theme.breakpoints.down('xs')} {
      padding: 24px 0 0;
    }
  `,
  skillsListItem: (theme: Theme) => css`
    display: flex;
    justify-content: space-between;
    padding-bottom: 8px;
    padding-top: 8px;
    border-bottom: 1px solid ${theme.palette.gray[300]};

    &:first-of-type {
      padding-top: 0;
    }

    &:last-of-type {
      padding-bottom: 0;
      border-bottom: none;
    }

    ${theme.breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  overallScoreProficiencyTextWrapper: css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
  `,

  skillProficiencyContainer: (theme: Theme) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 135px;

    ${theme.breakpoints.down('xs')} {
      margin-left: initial;
      width: 100%;
      justify-content: flex-start;
    }
  `,
  recruiterSupportText: (theme: Theme) => css`
    margin-top: ${theme.spacing(16)};
  `,
};

type PropsFromCaller = {
  testSummary?: SkillAssessmentsTestResults;
  desiredOccupation?: string;
  isRecruiter?: boolean;
};

export const SkillsAssessmentBreakdownPlaceholder = () => (
  <>
    <VisuallyHidden role="status">{_t('Loading skills assessment data')}</VisuallyHidden>

    <Grid css={styles.skillsDataContainer} container>
      <Grid
        css={styles.overallScoreSection}
        xs={12}
        sm={4}
        md={3}
        item
        container
        alignItems="center"
        justifyContent="center"
        direction="column"
      >
        <Skeleton variant="rect" width="100%" height="100%" />
      </Grid>

      <Grid xs={12} sm={8} md={9} item container>
        <ul css={styles.skillsList}>
          {[1, 2, 3, 4, 5, 6].map((entry) => {
            return (
              <li css={styles.skillsListItem} key={`skill-placeholder${entry}`}>
                <Skeleton width="100%" />
              </li>
            );
          })}

          <li css={styles.skillsListItem}>
            <Skeleton width="100%" />
          </li>
        </ul>
      </Grid>
    </Grid>
  </>
);

export const SkillsAssessmentBreakdown = ({ testSummary, desiredOccupation, isRecruiter }: PropsFromCaller) => {
  const theme = useTheme();
  const { isRolesMapLoading } = useContext(OccupationRolesContext);
  const { isProfileLoading, editable } = useContext(ProfileContext);
  const proficiencyMapping = getProficiencyIdToLabel();

  if (isRolesMapLoading || isProfileLoading) {
    return <SkillsAssessmentBreakdownPlaceholder />;
  }

  if (!testSummary || !testSummary.result) {
    return null;
  }

  const proficiency = testSummary.result.proficiency;
  const overallProficiency = proficiencyMapping[proficiency];
  const proficiencyColors = getColorByProficiency(theme, proficiency);
  const backgroundColor = proficiencyColors?.tagColor;
  const overallScoreEllipseColor = proficiencyColors?.dotColor;

  return (
    <>
      <Grid data-testid="skills-assessment-data" css={styles.skillsDataContainer} container>
        <Grid
          css={[backgroundColor && styles.overallScoreBackground(backgroundColor), styles.overallScoreSection]}
          xs={12}
          sm={4}
          md={3}
          item
          container
          alignItems="center"
          justifyContent="center"
          direction="column"
        >
          <div data-testid="overall-data">
            <Typography variant="d1" align="center">
              {testSummary.result.score}%
            </Typography>
            <div css={[styles.overallScoreProficiencyTextWrapper]}>
              <div css={overallScoreEllipseColor && styles.filledEllipse(overallScoreEllipseColor)} />
              <Typography variant="h2semibold" align="center">
                {overallProficiency}
              </Typography>
            </div>
          </div>
        </Grid>

        <Grid xs={12} sm={8} md={9} item>
          <ul css={styles.skillsList}>
            {/* I make a copy of the array to avoid sorting in place which sometimes throws an error */}
            {[...testSummary.result.individualSkillAssessments]
              .sort((assessment1, assessment2) => assessment2.score - assessment1.score)
              .map((assessment) => {
                const skillProficiencyColors = getColorByProficiency(theme, assessment.proficiency);
                const skillEllipseColor = skillProficiencyColors?.dotColor;

                return (
                  <li css={styles.skillsListItem} key={assessment.skillName}>
                    <Typography2 component="h3" variant="subtitleMedium">
                      {assessment.skillName}
                    </Typography2>
                    <div css={styles.skillProficiencyContainer}>
                      <div css={skillEllipseColor && styles.filledEllipse(skillEllipseColor)} />

                      <Typography2 component="p" variant="bodySecondary">
                        <strong>{assessment.score}% </strong>
                        {_t('(#{proficiencyLabel})', {
                          proficiencyLabel: proficiencyMapping[assessment.proficiency],
                        })}
                      </Typography2>
                    </div>
                  </li>
                );
              })}
          </ul>
        </Grid>
      </Grid>
      {isRecruiter && desiredOccupation && !editable && (
        <Typography2 component="p" color="supportText" css={styles.recruiterSupportText} variant="bodySecondary">
          {_t(
            'Scores are based on the completion of an industry leading job-aligned skill assessment for the role: Entry-level #{role}.',
            { role: desiredOccupation }
          )}
        </Typography2>
      )}
    </>
  );
};

export default SkillsAssessmentBreakdown;
